import {Paper, styled, tooltipClasses} from "@mui/material";
import CameraCreate from "../pages/cameraCreate";
import FileCreate from "../pages/fileCreate";
import {AIModelHeaders, ApplicationHeaders, FirmwareHeaders} from "./getFields";
import {aiModelFields, applicationFields, cameraFields, firmwareFields} from "./modelFields";
import {AIModelSearchFields, ApplicationSearchFields, FirmwareSearchFields} from "./searchFields";
import React, {useEffect, useRef, useState} from "react";
import Tooltip from "@mui/material/Tooltip";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {Check, Close} from "@mui/icons-material";
import {DatePicker} from "@mui/x-date-pickers";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// Configure Day.js to use UTC and Timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const PAGE_SIZE = 20;

export const updateData = (obj, headers, prefix, enums) => {
    if (Object.keys(obj).length === 0) {
        return obj;
    }
    const userTimezone = dayjs.tz.guess();

    let newObj = Object.keys(obj).reduce((acc, key) => {
        if (!headers || headers.includes(key)) {
            if (enums && Object.keys(enums).includes(key)) {
                if (Array.isArray(obj[key])) {
                    acc[key] = obj[key].map((item) => (enums[key].data[item] || item));
                } else {
                    acc[key] = enums[key].data[obj[key]] || obj[key];
                }
            } else {
                acc[key] = obj[key];
            }
        }

        return acc;
    }, {})

    if (newObj[`${prefix}_content`]) {
        newObj[`${prefix}_content`] = newObj[`${prefix}_content`].split('/').pop();
    }

    if (newObj[`${prefix}_timeAdded`]) {
        const {datetimeStr} = toHumanReadableTime(newObj[`${prefix}_timeAdded`], userTimezone)
        newObj[`${prefix}_timeAdded`] = datetimeStr;
    }

    if (newObj[`${prefix}_timeDismissed`]) {
        const {datetimeStr} = toHumanReadableTime(newObj[`${prefix}_timeDismissed`], userTimezone)
        newObj[`${prefix}_timeDismissed`] = datetimeStr;
    }

    if (newObj[`${prefix}_timeLastSynced`]) {
        const {datetimeStr} = toHumanReadableTime(newObj[`${prefix}_timeLastSynced`], userTimezone)
        newObj[`${prefix}_timeLastSynced`] = datetimeStr;
    }

    if (newObj[`last_update_time`]) {
        const {datetimeStr} = toHumanReadableTime(newObj.last_update_time, userTimezone)
        newObj.last_update_time = datetimeStr;
    }

    const {creator: _, ...finalObj} = newObj;

    return finalObj;
}

export function stringToHexColor(str, rgb = false, alpha = 0.85) {
    let hash = 0x811c9dc5; // 32-bit FNV-1a hash initialization
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash = (hash * 0x01000193) >>> 0; // Multiply by FNV prime and ensure it's a 32-bit number
    }

    let hexColor = '';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        hexColor += ('00' + value.toString(16)).slice(-2);
    }
    if (rgb) {
        let bigint = parseInt(hexColor, 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return `#${hexColor}`;
}

export const CustomTooltip = ({active, payload, label}) => {
    if (active && payload && payload.length) {
        return (
            <Paper sx={{p: 1}}>
                <Typography variant='body2' key='datetime' gutterBottom>{payload[0].payload.toolTipTime}</Typography>
                {payload.map(item => <Typography
                    variant='body2'
                    key={item.name}
                >
                    {item.name} : {item.value}
                </Typography>)}
            </Paper>
        );
    }

    return null;
};

export const updateConfirmationMessage = (objList) => {
    let newObjList = objList.sort((a, b) => {
        return new Date(b.timestamp) - new Date(a.timestamp);
    })
    const userTimezone = dayjs.tz.guess();

    newObjList = newObjList.map((obj) => {
        let newObj = {...obj};
        if (newObj.timestamp) {
            const {datetimeStr} = toHumanReadableTime(newObj.timestamp, userTimezone);
            newObj = {...newObj, timestamp: datetimeStr};
        }
        return newObj;
    })
    return newObjList;
}

export const toHumanReadableTime = (timeString, timezone, short = false) => {
    if (!timeString) {
        return {timeStr: "N/A", datetimeStr: "N/A"};
    }
    let datetimeStr, timeStr;
    if (short) {
        datetimeStr = dayjs.utc(timeString).tz(timezone).format('YYYY-MM-DD HH:mm:ss');
        timeStr = dayjs.utc(timeString).tz(timezone).format('HH:mm:ss');
    } else {
        datetimeStr = dayjs.utc(timeString).tz(timezone).format('YYYY-MM-DD HH:mm:ss UTCZ');
        timeStr = dayjs.utc(timeString).tz(timezone).format('HH:mm:ss UTCZ');
    }

    return {timeStr, datetimeStr};
};

export const timestampToTimeString = (timestamp, short = false) => {
    if (short) {
        return {
            datetime: dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss'),
            time: dayjs(timestamp).format('HH:mm:ss')
        };
    } else {
        return {
            datetime: dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss UTCZ'),
            time: dayjs(timestamp).format('HH:mm:ss UTCZ')
        };
    }
}
const getKeyByGranularity = (granularity, timezone, data) => {
    switch (granularity) {
        case 'hour':
            return {
                ...data,
                dateKey: data.timeObj.toLocaleString('default', {
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZone: timezone
                }),
                toolTipTime: data.timeObj.toLocaleString('default', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZone: timezone
                })
            };
        case 'day':
            return {
                ...data,
                dateKey: data.timeObj.toLocaleString('default', {hour: 'numeric', timeZone: 'UTC'}),
                toolTipTime: data.timeObj.toLocaleString('default', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZone: 'UTC'
                })
            };
        case 'week':
            return {
                ...data,
                dateKey: data.timeObj.toLocaleString('default', {weekday: 'short', timeZone: 'UTC'}),
                toolTipTime: data.timeObj.toLocaleString('default', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    timeZone: 'UTC'
                })
            };
        case 'month':
            return {
                ...data,
                dateKey: data.timeObj.toLocaleString('default', {month: 'short', day: 'numeric', timeZone: 'UTC'}),
                toolTipTime: data.timeObj.toLocaleString('default', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    timeZone: 'UTC'
                })
            };
        case 'year':
            return {
                ...data,
                dateKey: data.timeObj.toLocaleString('default', {month: 'long', timeZone: 'UTC'}),
                toolTipTime: data.timeObj.toLocaleString('default', {
                    year: 'numeric',
                    month: 'long',
                    timeZone: 'UTC'
                })
            };
        default:
            return {
                ...data, dateKey: data.timeObj.toLocaleString('default', {hour: 'numeric', minute: '2-digit'}),
                toolTipTime: data.timeObj.toLocaleString('default', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZone: timezone
                })
            };
    }
}

export const convertRecordTime = (obj, granularity) => {
    if (obj.r_timeAdded) {
        let newObj = {...obj};
        const userTimezone = dayjs.tz.guess();
        newObj = {...newObj, timeObj: new Date(newObj.r_timeAdded)}
        return getKeyByGranularity(granularity, userTimezone, newObj);
    }
    return obj;
}

export const updateAlert = (obj, enums) => {
    let newObj = {...obj};
    const userTimezone = dayjs.tz.guess();

    if (newObj.al_timeAdded) {
        const {timeStr, datetimeStr} = toHumanReadableTime(newObj.al_timeAdded, userTimezone, true);
        newObj = {...newObj, time: timeStr, datetime: datetimeStr}
    }
    if (enums && Object.keys(enums).length > 0) {
        newObj = {
            ...newObj,
            al_severity: enums?.al_severity ? enums.al_severity.data[obj.al_severity] : obj.al_severity,
            al_status: enums?.al_status ? enums.al_status.data[obj.al_status] : obj.al_status
        }
    }
    return newObj;
}

export const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export const sleep = ms => new Promise(r => setTimeout(r, ms));

export const getRequiredFields = (fields) => {
    return Object.values(fields).filter(item => item.required).map(item => item.alias)
}

export const getCreateFields = (fields) => {
    return Object.values(fields).filter(item => item.create)
}

export const isDict = (obj) => {
    return typeof obj === 'object' && obj !== null && Object.getPrototypeOf(obj) === Object.prototype;
};

export const convertUnitDataList = (units, fields) => {
    return units.reduce((acc, item, _) => ({
        ...acc,
        [item.id] : convertUnitData(item, fields)
    }), {});
}

export const convertUnitData = (unit, fields) => {
    return Object.entries(unit).reduce((acc, [key, value]) => {
            if (key === 'id') {
                return acc;
            }
            if (key === 'u_cam_issue') {
                acc[fields[key].display] =
                    <OverflowTooltip>{value ? 'Camera Malfunction' : 'All Camera Functional'}</OverflowTooltip>;
            } else if (key === 'u_wifi') {
                acc[fields[key].display] = <OverflowTooltip>{value ? 'Online' : 'Offline'}</OverflowTooltip>;
            } else if (typeof value === 'boolean') {
                acc[fields[key].display] = value ? <Check/> : <Close/>;
            } else if (Array.isArray(value)) {
                acc[fields[key].display] =
                    <OverflowTooltip>{value.reduce((str, item) => (str ? `${str}, ${item}` : item), '')}</OverflowTooltip>;
            } else {
                acc[fields[key].display] = <OverflowTooltip>{value}</OverflowTooltip>;
            }
            return acc;
        }, {})
}

export const relationContent = {
    'Camera': (setId, setClose) => <CameraCreate fields={cameraFields} setId={setId} setClose={setClose}/>,
    'Firmware': (setId, setClose) => <FileCreate fields={firmwareFields} alias='firmware' setId={setId}
                                                 setClose={setClose}/>,
    'Application': (setId, setClose) => <FileCreate fields={applicationFields} alias='application'
                                                    setId={setId} setClose={setClose}/>,
    'AIModel': (setId, setClose) => <FileCreate fields={aiModelFields} alias='aimodel' setId={setId}
                                                setClose={setClose}/>,
    'CameraProperty': null,
    'AccountProperty': null
}

export const fileModel = {
    firmware: {
        prefix: 'f',
        propertyPrefix: 'fp',
        display: 'Firmware',
        fileHeaders: FirmwareHeaders,
        fields: firmwareFields,
        searchFields: FirmwareSearchFields,
        property: 'FirmwareProperty'
    },
    aimodel: {
        prefix: 'am',
        propertyPrefix: 'amp',
        display: 'AI Model',
        fileHeaders: AIModelHeaders,
        fields: aiModelFields,
        searchFields: AIModelSearchFields,
        property: 'AIModelProperty'
    },
    application: {
        prefix: 'app',
        propertyPrefix: 'apr',
        display: 'Application',
        fileHeaders: ApplicationHeaders,
        fields: applicationFields,
        searchFields: ApplicationSearchFields,
        property: 'ApplicationProperty'
    }
}

export const LightTooltip = styled(({className, ...props}) => (
    <Tooltip {...props} classes={{popper: className}}/>))(({theme}) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: 'rgba(240, 240, 240, 0.90)',
    }, [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: 'rgba(240, 240, 240, 0.90)',
        color: 'rgba(50, 50, 50, 0.90)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

export const OverflowTooltip = ({children}) => {
    const textRef = useRef(null);
    const [isOverflowed, setIsOverflowed] = useState(false);

    useEffect(() => {
        const checkOverflow = () => {
            const {current} = textRef;
            if (current) {
                setIsOverflowed(current.scrollWidth > current.clientWidth || current.scrollHeight > current.clientHeight);
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [children]);

    return (
        <LightTooltip title={children} disableHoverListener={!isOverflowed} leaveDelay={300} placement="bottom-start">
            <div ref={textRef} style={{
                whiteSpace: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical'
            }}>
                {children}
            </div>
        </LightTooltip>);
};

export const generateImageName = (c_sn, timeString) => {
    const userTimezone = dayjs.tz.guess();
    const datetimeString = dayjs.utc(timeString).tz(userTimezone).format('YYYY-MM-DD-HH-mm-ss');
    return c_sn + '_' + datetimeString;
}

export const SelectOnlyDatePicker = (props) => {
    const {children, slotProps, ...otherProps} = props;
    const [open, setOpen] = useState(false);
    return (
        <DatePicker
            {...otherProps}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            enableAccessibleFieldDOMStructure={false}
            slots={{
                textField: TextField
            }}
            slotProps={{
                ...slotProps,
                field: {
                    ...slotProps?.field,
                    readOnly: true
                },
                textField: {
                    ...slotProps?.textField,
                    inputProps: {
                        ...slotProps?.textField?.inputProps,
                        readOnly: true,
                        onKeyDown: (e) => e.preventDefault(),
                        onClick: () => setOpen(true),
                        style: {cursor: 'pointer'}
                    }
                }
            }}
        >
            {children}
        </DatePicker>
    );
}